/**
 * @author agr
 */
import { render, html, nothing } from "lit-html";
import { repeat } from "lit-html/directives/repeat.js";
import { classMap } from "lit-html/directives/class-map.js";
import { styleMap } from "lit-html/directives/style-map.js";
import { trackPageOnce, track } from "../tracker/EventTracker";
import { stateful } from "../../shared/components/element-builder";
import { center } from "../dom/touch";
import { isAtLeast } from "../dom/screen";
import "../../shared/components/Picture";

const SWIPE_THRESHOLD = 0.2;

class PictureGallery extends HTMLElement {
	static getInitialState() {
		return {
			currentPicture: 0,
			currentDelta: 0,
			swipeDelta: null,
		};
	}

	constructor() {
		super();
		this.lastX = 0;
		this.lastY = 0;
		this.cancelled = false;

		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
	}

	connectedCallback() {
		this.trackName = this.getAttribute("track-name");
		this.trackNameSuffix = this.getAttribute("track-name-suffix") || "";
		this.categoryName = this.getAttribute("track-category") || "Hotel";
		this.visiblePictureCount =
			parseInt(this.getAttribute("visible-pictures")) || 1;
		this.preloadedPictureCount =
			parseInt(this.getAttribute("preloaded-pictures")) || 2;
		this.disableLoop = this.hasAttribute("disable-loop");
		this.resetScroller();

		this.addEventListener("touchstart", this.onTouchStart);
		this.addEventListener("touchmove", this.onTouchMove);
		this.addEventListener("touchend", this.onTouchEnd);
	}

	disconnectedCallback() {
		this.removeEventListener("touchstart", this.onTouchStart);
		this.removeEventListener("touchmove", this.onTouchMove);
		this.removeEventListener("touchend", this.onTouchEnd);
	}

	render() {
		const carouselPictures = this.getCarouselPictures(
			this.currentPicture - this.currentDelta,
		);
		const renderPicture =
			this.customRenderPicture || this.renderPicture.bind(this);
		let transition = "none";

		let position = 0;
		if (this.swipeDelta !== null) {
			position = this.swipeDelta;
		} else if (this.currentDelta != 0) {
			transition = "transform";
			const spacing = this.getAttribute("picture-margin")
				? parseInt(this.getAttribute("picture-margin"))
				: 0;
			const picWidth = this.getAttribute("picture-width")
				? this.getAttribute("picture-width")
						.split(",")
						.map(definition => definition.split(":"))
						.filter(([size]) => isAtLeast(size))
						.map(([, width]) => parseInt(width))
						.slice(-1)[0] + spacing
				: this.getBoundingClientRect().width;
			position = -1 * this.currentDelta * picWidth;
		}

		const scrollerStyle = styleMap({
			transitionProperty: transition,
			transform: `translateX(${position}px)`,
		});

		render(
			html`
				${this.hasAttribute("enable-loader")
					? html`<div class="picture-gallery__spinner">
							<div class="spinner"></div>
						</div>`
					: nothing}

				<div
					class="picture-gallery__scroller"
					style=${scrollerStyle}
					@transitionend=${this.onTransitionEnd}
					data-gallery-index=${this.currentPicture}
				>
					${repeat(
						carouselPictures,
						([, { filename }]) => filename,
						pair =>
							renderPicture(...pair, this.renderPicture.bind(this, ...pair)),
					)}
				</div>

				${this.hasAttribute("enable-counter")
					? html`<span class="picture-gallery__counter is-translucent"
							>${this.currentPicture + 1} / ${this.pictures.length}</span
						>`
					: nothing}

				<div class="picture-gallery__overlay">
					${this.beforeOverlay || nothing}
					${this.getRelativeIndices(this.visiblePictureCount).map(
						delta =>
							html` <div
								class=${classMap({
									"picture-gallery__overlay-picture": true,
									"picture-gallery__overlay-picture--current": delta === 0,
								})}
								@click=${this.onClickPicture(delta)}
							></div>`,
					)}
				</div>
				${this.afterOverlay || nothing}
				${this.hasAttribute("enable-arrows")
					? ["left", "right"].map(
							half => html`
								<div
									class="picture-gallery__overlay-${half}"
									@click=${this.onClickArrow(half)}
								>
									<i class="fa fa-angle-${half}"></i>
								</div>
							`,
						)
					: nothing}
			`,
			this,
			{ host: this },
		);
	}

	renderPicture(idx, { filename, alt }) {
		const realIndex = this.getPictureIndex(idx);
		const shadowed =
			(realIndex > this.currentPicture && idx < 0) ||
			(realIndex < this.currentPicture && idx >= this.pictures.length);

		const classes = classMap({
			"picture-gallery__image": true,
			"picture-gallery__image--current":
				idx === this.currentPicture && !this.swipeDelta,
			"picture-gallery__image--shadowed": shadowed,
		});

		if (shadowed && this.disableLoop) {
			// No need to load this picture, as we cannot loop and it's over the
			// limit: just add a placeholder for positioning.
			return html`<div class=${classes}></div>`;
		}

		return html`
			<esc-picture
				class=${classes}
				alt="${alt}"
				type="hotel"
				filename="${filename}"
				sizes=${this.getAttribute("sizes")}
				?force-load=${this.hasAttribute("force-load")}
				?onhold=${this.onhold}
				?disable-webp=${this.hasAttribute("disable-webp")}
			></esc-picture>
		`;
	}

	onTransitionEnd() {
		this.resetScroller();
		this.dispatchEvent(
			new CustomEvent("AfterPictureChanged", { bubbles: true }),
		);
	}

	onTouchStart({ touches, changedTouches }) {
		if (!this.getImageWidth()) {
			// Wait till the image is loaded.
			this.cancelled = true;
			return;
		}

		// Reset needed when swiping is too fast.
		if (touches.length === changedTouches.length) {
			this.resetScroller();
			this.swipeDelta = 0;
			this.cancelled = null;
		}

		const [x, y] = center(touches);
		this.lastX = x;
		this.lastY = y;
	}

	onTouchMove(event) {
		if (this.cancelled === true) {
			return;
		}

		const [x, y] = center(event.touches);
		const deltaX = x - this.lastX;
		const deltaY = y - this.lastY;
		if (
			this.disableLoop &&
			((this.currentPicture === 0 && deltaX > 0) ||
				(this.currentPicture === this.pictures.length - 1 && deltaX < 0))
		) {
			return;
		}

		if (this.cancelled === null) {
			const angle =
				(Math.atan2(Math.abs(deltaX), Math.abs(deltaY)) * 180) / Math.PI;
			if (angle < 10) {
				this.cancelled = true;
				return;
			}
			this.cancelled = false;
		}

		event.preventDefault();
		if (!event.inhibit) {
			// Hack: don't swipe if the zooming layer inhibited the event. Stopping
			// propagation doesn't work because we wouldn't know the last coordinates.
			this.swipeDelta += deltaX;
		}
		this.lastX = x;
		this.lastY = y;
	}

	onTouchEnd({ touches, changedTouches }) {
		if (this.cancelled === true || touches.length > changedTouches.length) {
			// Recalculate start position to avoid jerking.
			const [x, y] = center(touches);
			this.lastX = x;
			this.lastY = y;
			return;
		}

		const deltaImage = (-1 * this.swipeDelta) / this.getImageWidth();
		let indexDelta = Math.trunc(deltaImage);
		const deltaFrac = deltaImage - indexDelta;
		if (deltaFrac >= SWIPE_THRESHOLD) {
			indexDelta++;
		}
		if (deltaFrac <= -SWIPE_THRESHOLD) {
			indexDelta--;
		}

		this.swipeDelta = null;
		this.go(this.currentPicture + indexDelta, indexDelta);

		if (indexDelta !== 0) {
			const directionLabel = indexDelta < 0 ? "Zurück" : "Vor";
			if (this.trackName == "Thumbnails") {
				track(
					this.categoryName,
					`${this.trackName} Swipe${this.trackNameSuffix}`.trim(),
					`${directionLabel} | ${Math.abs(indexDelta)}`,
				);
			} else {
				const labelSuffix = this.getAttribute("track-label-suffix") || "";
				trackPageOnce(
					this.categoryName,
					`${this.trackName} Swipe${this.trackNameSuffix}`.trim(),
					`${directionLabel} ${labelSuffix}`.trim(),
				);
			}
		}
	}

	onClickArrow(half) {
		return () => {
			this.goOne(half);
			trackPageOnce(
				this.categoryName,
				`${this.trackName} Klick`.trim(),
				`${half == "left" ? "Zurück" : "Vor"}`,
			);
		};
	}

	onClickPicture(indexDelta) {
		return () => {
			const targetIndex = this.currentPicture + indexDelta;
			if (
				indexDelta == 0 ||
				targetIndex < 0 ||
				targetIndex >= this.pictures.length
			) {
				return;
			}
			this.go(targetIndex, indexDelta);
			track(
				this.categoryName,
				`${this.trackName} Klick`.trim(),
				`${indexDelta < 0 ? "Zurück" : "Vor"} | ${Math.abs(indexDelta)}`,
			);
		};
	}

	goOne(half) {
		const indexDelta = half == "left" ? -1 : 1;
		this.go(this.currentPicture + indexDelta, indexDelta);
	}

	go(currentPicture, currentDelta) {
		if (Math.abs(currentDelta) > this.preloadedPictureCount) {
			currentDelta = 0;
		}

		currentPicture = this.getPictureIndex(currentPicture);
		if (this.currentDelta != currentDelta) {
			this.currentDelta = currentDelta;
		}

		if (this.currentPicture != currentPicture) {
			this.currentPicture = currentPicture;
			this.dispatchEvent(
				new CustomEvent("PictureChanged", {
					bubbles: true,
					detail: { currentPicture, currentDelta },
				}),
			);
		}
	}

	resetScroller() {
		this.go(this.currentPicture, 0);
	}

	getCarouselPictures(anchorPicture) {
		return this.getRelativeIndices(
			this.visiblePictureCount + 2 * this.preloadedPictureCount,
		).map(delta => [
			anchorPicture + delta,
			this.pictures[this.getPictureIndex(anchorPicture + delta)],
		]);
	}

	getRelativeIndices(count) {
		const deltaLimit = Math.floor((count - 1) / 2);
		const indices = [];
		for (let delta = -deltaLimit; delta <= deltaLimit; delta++) {
			indices.push(delta);
		}
		return indices;
	}

	getPictureIndex(index) {
		index = index % this.pictures.length;
		if (index < 0) {
			index = this.pictures.length + index;
		}

		return index;
	}

	getImageWidth() {
		if (!this.imageWidth) {
			this.imageWidth = this.querySelector("img").getBoundingClientRect().width;
		}
		return this.imageWidth;
	}
}

customElements.define("esc-picture-gallery", stateful(PictureGallery));
